export const PORTAL_ATTACHMENT_ONLY_PLACEHOLDER = '(Fil vedhæftet)';

export interface PortalRegisterDocumentArgs {
  clinicId: string;
  patientId: string;
  filename: string;
  originalFilename: string;
  mimeType: string;
  sizeBytes: number;
  storagePath: string;
  tags: ['message'];
}

export interface PortalComposerDeps {
  sendText(clinicId: string, content: string): Promise<{ id: string }>;
  uploadFile(file: File, storagePath: string): Promise<void>;
  registerDocument(args: PortalRegisterDocumentArgs): Promise<{ id: string }>;
  sendWithDocuments(
    clinicId: string,
    content: string,
    documentIds: string[],
  ): Promise<{ id: string }>;
  cleanupUpload(storagePath: string): Promise<void>;
}

export interface PortalComposerInput {
  clinicId: string;
  patientId: string | null;
  content: string;
  files: File[];
}

export function safePortalFilename(name: string): string {
  return name.replace(/[^a-zA-Z0-9._-]/g, '_');
}

export function portalAttachmentStoragePath(input: {
  clinicId: string;
  patientId: string;
  documentSegment: string;
  filename: string;
}): string {
  return `${input.clinicId}/${input.patientId}/${input.documentSegment}/${safePortalFilename(input.filename)}`;
}

export function portalComposerContent(content: string, fileCount: number): string {
  const trimmed = content.trim();
  if (trimmed) return trimmed;
  if (fileCount > 0) return PORTAL_ATTACHMENT_ONLY_PLACEHOLDER;
  throw new Error('empty_message');
}

/**
 * Text-only messages use send_portal_message.
 * A message with files is sent only after every file is uploaded and registered.
 * The message row and attachment rows then commit together.
 * Any uploaded path is discarded when registration or send fails, and the
 * composer is left in place because this function rejects.
 */
export async function sendPortalComposerMessage(
  input: PortalComposerInput,
  deps: PortalComposerDeps,
): Promise<{ id: string }> {
  if (!input.clinicId) {
    throw new Error('no_clinic');
  }

  const files = input.files;
  const content = portalComposerContent(input.content, files.length);

  if (files.length === 0) {
    return deps.sendText(input.clinicId, content);
  }

  if (!input.patientId) {
    throw new Error('no_patient');
  }

  const uploaded: string[] = [];
  try {
    const documentIds: string[] = [];
    for (const file of files) {
      const storagePath = portalAttachmentStoragePath({
        clinicId: input.clinicId,
        patientId: input.patientId,
        documentSegment: crypto.randomUUID(),
        filename: file.name,
      });
      await deps.uploadFile(file, storagePath);
      uploaded.push(storagePath);
      const document = await deps.registerDocument({
        clinicId: input.clinicId,
        patientId: input.patientId,
        filename: safePortalFilename(file.name),
        originalFilename: file.name,
        mimeType: file.type,
        sizeBytes: file.size,
        storagePath,
        tags: ['message'],
      });
      documentIds.push(document.id);
    }

    return await deps.sendWithDocuments(input.clinicId, content, documentIds);
  } catch (error) {
    for (const storagePath of uploaded) {
      try {
        await deps.cleanupUpload(storagePath);
      } catch (cleanupError) {
        console.error('Failed to discard portal upload', storagePath, cleanupError);
      }
    }
    throw error;
  }
}

export interface PortalMessageGateway {
  rpc(
    fn: string,
    args: Record<string, unknown>,
  ): PromiseLike<{ data: unknown; error: { message: string } | null }>;
  storage: {
    from(bucket: string): {
      upload(
        path: string,
        file: File,
        options: { upsert: boolean },
      ): PromiseLike<{ error: { message: string } | null }>;
      remove(paths: string[]): PromiseLike<{ error: { message: string } | null }>;
    };
  };
}

function rpcError(error: { message: string } | null): void {
  if (error) throw new Error(error.message);
}

export function portalMessageGateway(client: PortalMessageGateway): PortalComposerDeps {
  return {
    async sendText(clinicId, content) {
      const { data, error } = await client.rpc('send_portal_message', {
        p_clinic_id: clinicId,
        p_content: content,
      });
      rpcError(error);
      return data as { id: string };
    },
    async uploadFile(file, storagePath) {
      const { error } = await client.storage.from('patient-documents').upload(storagePath, file, {
        upsert: false,
      });
      rpcError(error);
    },
    async registerDocument(args) {
      const { data, error } = await client.rpc('register_document', {
        p_clinic_id: args.clinicId,
        p_patient_id: args.patientId,
        p_filename: args.filename,
        p_original_filename: args.originalFilename,
        p_mime_type: args.mimeType,
        p_size_bytes: args.sizeBytes,
        p_storage_path: args.storagePath,
        p_tags: args.tags,
      });
      rpcError(error);
      return data as { id: string };
    },
    async sendWithDocuments(clinicId, content, documentIds) {
      const { data, error } = await client.rpc('send_portal_message_with_documents', {
        p_clinic_id: clinicId,
        p_content: content,
        p_document_ids: documentIds,
      });
      rpcError(error);
      return data as { id: string };
    },
    async cleanupUpload(storagePath) {
      const discarded = await client.rpc('discard_unlinked_portal_upload', {
        p_storage_path: storagePath,
      });
      if (discarded.error) {
        throw new Error(discarded.error.message);
      }
      const removed = await client.storage.from('patient-documents').remove([storagePath]);
      rpcError(removed.error);
    },
  };
}
